import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DRISHTI_LIFECYCLE_STATE } from '../constants/drishtiLifecycle';
import type { DrishtiLifecycleState } from '../constants/drishtiLifecycle';
import { BLANK_STATION_CONFIG } from '../constants/stationConfig';
import type { StationConfig, StationConfigDraft } from '../constants/stationConfig';
import type {
    CalibrationFailure,
    CalibrationStatus,
    CalibrationStatusResponse,
    StartCalibrationResponse,
} from '../services/drishtiHandler';

export type CalibrationPhase = 'test' | 'finalize';

/**
 * Sub-state that tracks the current calibration run for the device the installer is installing.
 * Persisted alongside the rest of drishtiInstallConfig via the existing 12h expireReducer
 * so a run that starts on one app session can be resumed on the next.
 */
export interface CalibrationState {
    // Which phase the installer is in. Null before any calibration has started.
    // 'test' = dry run to verify marker placement; 'finalize' = real calibration.
    phase: CalibrationPhase | null;
    // The latest status observed. Null before Calibrate has been tapped for the first time.
    status: CalibrationStatus | null;
    // The current calibration run identifier from the installer backend, used for logging
    // and diagnostic correlation. Null before the first poll response arrives.
    calibrationId: string | null;
    // Structured failure payload from the installer backend, present when status === FAILED.
    // Carries the failure code, retryability flag, and the remediation list rendered on the
    // failure sub-screen. Null while a run is in progress or when it succeeded.
    failure: CalibrationFailure | null;
    // Most recent transport-level error message from a start/poll call. Cleared on the next
    // successful poll. Drives the "Connection lost, retrying…" alert on whichever sub-screen
    // is currently showing.
    lastError: string | null;
    // Total number of calibration attempts for this device on this install, including the
    // initial run. Increments on every startCalibration call. Compared against RETRY_CAP on
    // the failure sub-screen to decide between "show Retry" and "show escalation". Reset only
    // when the install it belongs to is dropped — calibrationReset preserves it so the counter
    // survives across Retry clicks.
    attemptCount: number;
    // Server-side escalation flag from completeCalibration. When true, the failure screen
    // shows the escalation copy (no Retry button). Takes precedence over the local
    // attemptCount >= RETRY_CAP check when available.
    escalated: boolean;
    // Epoch-ms timestamp captured when the current attempt started (i.e. when calibrationStarted
    // last fired). Powers the elapsed-time display on the progress screen, and — because it
    // is persisted with the rest of the slice — survives a refresh so the timer resumes from
    // the actual request start rather than restarting from zero. Null before the first attempt,
    // reset on every Retry (new attempt = new start), cleared when the install it belongs to is dropped.
    currentAttemptStartedAtMs: number | null;
    // ISO timestamp from the last poll response — used on the success screen to show when
    // calibration completed (survives refresh via redux-persist).
    updatedAt: string | null;
    // True when the resume seeded an ACTIVE device, i.e. the station the success screen is showing
    // went live before this session rather than under the technician's eyes. Only the banner reads
    // it; every action that begins a run rebuilds the state from BLANK_CALIBRATION, so it cannot
    // survive into a run the technician did start.
    completedBeforeResume: boolean;
}

export interface InstallResumedPayload {
    readonly deviceSerial: string;
    readonly lifecycleState: DrishtiLifecycleState;
}

export interface DrishtiInstallConfigState {
    site: string | null;
    stationNumber: string | null;
    deviceSerialDraft: string | null;
    deviceSerial: string | null;
    stationConfig: StationConfigDraft;
    installStarted: boolean;
    installEnded: boolean;
    calibration: CalibrationState;
}

const BLANK_CALIBRATION: CalibrationState = {
    phase: null,
    status: null,
    calibrationId: null,
    failure: null,
    lastError: null,
    attemptCount: 0,
    escalated: false,
    currentAttemptStartedAtMs: null,
    updatedAt: null,
    completedBeforeResume: false,
};

export const initialState: DrishtiInstallConfigState = {
    site: null,
    stationNumber: null,
    deviceSerialDraft: null,
    deviceSerial: null,
    stationConfig: BLANK_STATION_CONFIG,
    installStarted: false,
    installEnded: false,
    calibration: BLANK_CALIBRATION,
};

// The sub-screen the calibration page opens on for a device resumed mid-calibration. Both phases
// end in SUCCEEDED, so `phase` is what separates a passed dry run (offer Finalize) from a live
// station (offer the next station). The pre-calibration states seed nothing and open idle.
const RESUMED_CALIBRATION: Record<DrishtiLifecycleState, Partial<CalibrationState>> = {
    [DRISHTI_LIFECYCLE_STATE.INSTALL_STARTED]: {},
    [DRISHTI_LIFECYCLE_STATE.INSTALL_ENDED]: {},
    [DRISHTI_LIFECYCLE_STATE.DRY_RUN_PENDING]: { phase: 'test', status: 'IN_PROGRESS' },
    [DRISHTI_LIFECYCLE_STATE.DRY_RUN_FAILED]: { phase: 'test', status: 'FAILED' },
    [DRISHTI_LIFECYCLE_STATE.DRY_RUN_PASSED]: { phase: 'test', status: 'SUCCEEDED' },
    [DRISHTI_LIFECYCLE_STATE.CALIBRATION_PENDING]: { phase: 'finalize', status: 'IN_PROGRESS' },
    [DRISHTI_LIFECYCLE_STATE.CALIBRATION_FAILED]: { phase: 'finalize', status: 'FAILED' },
    // Reachable from either phase and the state doesn't say which; the escalation screen never asks.
    [DRISHTI_LIFECYCLE_STATE.ESCALATED]: { status: 'FAILED', escalated: true },
    [DRISHTI_LIFECYCLE_STATE.ACTIVE]: { phase: 'finalize', status: 'SUCCEEDED' },
};

const dropInstall = (state: DrishtiInstallConfigState) => {
    state.installStarted = false;
    state.installEnded = false;
    state.deviceSerialDraft = null;
    state.deviceSerial = null;
    state.calibration = BLANK_CALIBRATION;
};

const dropResolvedStationAndInstall = (state: DrishtiInstallConfigState) => {
    state.stationConfig = BLANK_STATION_CONFIG;
    dropInstall(state);
};

export const drishtiInstallConfigSlice = createSlice({
    name: 'drishtiInstallConfig',
    initialState,
    reducers: {
        updateDrishtiDeviceSerialDraft: (state, action: PayloadAction<string>) => {
            state.deviceSerialDraft = action.payload;
        },
        updateDrishtiDeviceSerial: (state, action: PayloadAction<string>) => {
            state.deviceSerial = action.payload;
        },
        updateDrishtiSite: (state, action: PayloadAction<string>) => {
            state.site = action.payload;
            state.stationNumber = null;
            dropResolvedStationAndInstall(state);
        },
        updateDrishtiStationNumber: (state, action: PayloadAction<string>) => {
            if (state.stationNumber === action.payload) {
                return;
            }
            state.stationNumber = action.payload;
            dropResolvedStationAndInstall(state);
        },
        updateDrishtiStationConfig: (state, action: PayloadAction<StationConfig>) => {
            state.stationConfig = action.payload;
        },
        clearDrishtiStationConfig: (state) => {
            state.stationConfig = BLANK_STATION_CONFIG;
        },
        clearDrishtiInstall: dropInstall,
        updateDrishtiInstallStarted: (state, action: PayloadAction<boolean>) => {
            state.installStarted = action.payload;
            if (action.payload) {
                state.installEnded = false;
                state.calibration = BLANK_CALIBRATION;
            }
        },
        updateDrishtiInstallEnded: (state, action: PayloadAction<boolean>) => {
            state.installEnded = action.payload;
        },
        installResumed: (state, action: PayloadAction<InstallResumedPayload>) => {
            const { deviceSerial, lifecycleState } = action.payload;
            const seed = RESUMED_CALIBRATION[lifecycleState];
            state.deviceSerial = deviceSerial;
            state.installStarted = true;
            state.deviceSerialDraft = null;
            state.installEnded = lifecycleState !== DRISHTI_LIFECYCLE_STATE.INSTALL_STARTED;
            state.calibration = {
                ...BLANK_CALIBRATION,
                ...seed,
                // ACTIVE only. A passed dry run also resumes on SUCCEEDED, but it still has the
                // finalize run ahead of it, so its screen keeps the plain instructions.
                completedBeforeResume: lifecycleState === DRISHTI_LIFECYCLE_STATE.ACTIVE,
            };
        },
        // Called after startCalibration returns success. Sets the initial status, clears
        // any leftover error or failure from a previous run, increments attemptCount, and
        // stamps the current-attempt start timestamp so the progress screen's elapsed timer
        // can survive a mid-run refresh.
        //
        // The `prepare` callback stamps `startedAtMs: Date.now()` on the payload at dispatch
        // time, keeping the reducer itself pure and deterministic (a Redux best practice —
        // it preserves replay/time-travel debugging). Call sites keep the original single-arg
        // signature: `dispatch(calibrationStarted(response))`.
        calibrationStarted: {
            reducer: (
                state,
                action: PayloadAction<StartCalibrationResponse & { startedAtMs: number; phase: CalibrationPhase }>,
            ) => {
                state.calibration.phase = action.payload.phase;
                state.calibration.status = action.payload.status;
                state.calibration.failure = null;
                state.calibration.lastError = null;
                state.calibration.attemptCount += 1;
                state.calibration.currentAttemptStartedAtMs = action.payload.startedAtMs;
            },
            prepare: (response: StartCalibrationResponse, phase: CalibrationPhase) => ({
                payload: { ...response, startedAtMs: Date.now(), phase },
            }),
        },
        // Called on every successful poll. Updates status, records the failure payload
        // when present (on FAILED), and clears lastError since we just heard back.
        statusUpdated: (state, action: PayloadAction<CalibrationStatusResponse>) => {
            state.calibration.status = action.payload.status;
            state.calibration.calibrationId = action.payload.calibrationId;
            state.calibration.failure = action.payload.failure ?? null;
            state.calibration.lastError = null;
            state.calibration.updatedAt = action.payload.updatedAt ?? null;
            if (state.calibration.currentAttemptStartedAtMs === null) {
                const startedAtMs = Date.parse(action.payload.createdAt ?? '');
                state.calibration.currentAttemptStartedAtMs = Number.isNaN(startedAtMs) ? null : startedAtMs;
            }
        },
        // Called on a transport-level failure from startCalibration or pollCalibrationStatus.
        // Leaves status untouched so the current sub-screen keeps rendering; the alert layer
        // reads lastError to surface a "Connection lost, retrying…" banner.
        calibrationPollError: (state, action: PayloadAction<string>) => {
            state.calibration.lastError = action.payload;
        },
        // Dismisses the current transport-level error without touching status or the
        // failure payload. Used when the installer taps "Try again" on the Starting screen
        // to hide the error alert before the retry request goes out.
        calibrationErrorCleared: (state) => {
            state.calibration.lastError = null;
        },
        // Called when the installer taps Retry. Wipes the calibration status/failure/error so the
        // effect kicks off a fresh startCalibration; deviceSerial, site, stationNumber,
        // stationConfig and installStarted are kept because they describe the same physical
        // install.
        // attemptCount is intentionally preserved so retries accumulate toward RETRY_CAP.
        calibrationReset: (state) => {
            state.calibration = {
                ...BLANK_CALIBRATION,
                phase: state.calibration.phase,
                attemptCount: state.calibration.attemptCount,
            };
        },
        // Called when completeCalibration returns. Stores the server's escalation decision
        // so the failure screen uses server truth instead of local counter logic.
        calibrationCompleted: (state, action: PayloadAction<{ escalated: boolean; failedAttemptCount: number }>) => {
            state.calibration.escalated = action.payload.escalated;
        },
        // Called when the installer taps "Finalize calibration" after a successful test.
        // Resets calibration state for a fresh run but sets phase to 'finalize'.
        beginFinalizePhase: (state) => {
            state.calibration = { ...BLANK_CALIBRATION, phase: 'finalize' };
        },
        // A technician works one site for a whole shift, so finishing a station keeps the site and
        // sends them back to the station picker. Changing site is the navbar's job.
        startNewStation: (state) => {
            state.stationNumber = null;
            dropResolvedStationAndInstall(state);
        },
        clearDrishti: () => initialState,
    },
});

export const drishtiInstallConfigReducer = drishtiInstallConfigSlice.reducer;
export const {
    updateDrishtiDeviceSerialDraft,
    updateDrishtiDeviceSerial,
    updateDrishtiSite,
    updateDrishtiStationNumber,
    updateDrishtiStationConfig,
    clearDrishtiStationConfig,
    clearDrishtiInstall,
    updateDrishtiInstallStarted,
    updateDrishtiInstallEnded,
    installResumed,
    calibrationStarted,
    statusUpdated,
    calibrationPollError,
    calibrationErrorCleared,
    calibrationCompleted,
    beginFinalizePhase,
    calibrationReset,
    startNewStation,
    clearDrishti,
} = drishtiInstallConfigSlice.actions;
